import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Text } from 'grommet';
import { Refresh, Previous } from 'grommet-icons';
import Case from 'case';
import { useAppTheme } from '@Components/Context';
import {
  Seo,
  AppButton,
  Spinning,
  SimpleModal,
} from '@Components/Control';
import { ManualClaimStatusPill } from '@Components/Partial/Product/ContentId/ManualClaims/manualClaimUiHelpers.jsx';
import { withProductAuth } from '@Components/Layout';
import { AuthNavWrapper } from '@Components/Navigation';
import { paths } from '@Components/configs';
import { buildDateTimeLabel } from '@Helpers/util';
import {
  NoDataPlaceholder,
  TableWrapper,
  renderTableHeader,
} from '@Components/Partial/Product/SharedComponents.jsx';

/* eslint-disable camelcase -- manual claim API rows use snake_case (claim_type, asset_id, …) */
import {
  MANUAL_CLAIM_REQUEST_DETAIL_LINE_ITEMS_HEADER_MAP,
  STATUS_COLOR_MAP,
} from '@Components/Partial/Product/ContentId/constants';
import { StyledStatusBadge } from '@Components/Partial/Product/ListSearches/StyledListSearches.jsx';
import {
  initiateFetchManualClaimRequestDetail,
  clearManualClaimRequestDetail,
} from '@Actions';

function badgeBg(isDarkMode, page) {
  return isDarkMode ? page.altBg : page.inputBg;
}

const LINE_ITEMS_HEADER_MAP = MANUAL_CLAIM_REQUEST_DETAIL_LINE_ITEMS_HEADER_MAP.filter(
  (h) => !['match_meta', 'rejection_note', 'youtube_claim_id'].includes(h.uid),
).concat([
  { display: 'Details', uid: 'details_action', size: '12%' },
]);

const ManualClaimRequestDetailPage = ({
  small,
  location,
  detail,
  loading,
  fetchDetail,
  clearDetail,
  paymentPending,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme: { page, button }, isDarkMode } = useAppTheme();
  const usePageBg = isDarkMode ? page.background : page.altBg;
  const useSectionBg = isDarkMode ? page.altBg : page.background;

  const [detailsModal, setDetailsModal] = React.useState(null);
  const [notesModal, setNotesModal] = React.useState(null);

  React.useEffect(() => {
    if (!paymentPending && id) {
      fetchDetail(id);
    }
    return () => clearDetail();
  }, [id, paymentPending]);

  const items = detail?.items || [];
  const itemSummary = detail?.itemSummary || detail?.item_summary || {};
  // const submissionType = detail?.submissionType ?? detail?.submission_type;
  const createdAt = detail?.createdAt ?? detail?.created_at;
  const rejectionNote = detail?.rejectionNote ?? detail?.rejection_note;

  // const sourceLabel = submissionType === 'csv_upload' ? 'CSV upload' : 'Pasted rows';

  const renderLineItemsTable = () => {
    if (!items.length) {
      return (
        <NoDataPlaceholder
          noShadow
          noRound
          showRefreshButton={false}
          loading={false}
          label="No line items for this request."
          textColor={page.primaryText}
          loaderColor={page.highlight}
        />
      );
    }

    const renderDataPoint = (uid, row) => {
      if (uid === 'status') {
        return <ManualClaimStatusPill status={row.status} page={page} />;
      }
      if (uid === 'claim_action_type') {
        const label = row.claim_action_type ?? row.claim_type;
        return (
          <Text size="1rem" color={page.tableText} style={{ wordBreak: 'break-all' }}>
            {label || '—'}
          </Text>
        );
      }
      if (uid === 'details_action') {
        const hasNote = !!row.rejection_note;
        return (
          <Box direction="row" gap="0.5rem" wrap>
            <AppButton
              plain
              level="dynamicSmall"
              label="More Details"
              color={page.highlight}
              onClick={() => setDetailsModal(row)}
            />
            {hasNote && (
              <AppButton
                plain
                level="dynamicSmall"
                label="View Notes"
                color={page.highlight}
                onClick={() => setNotesModal(row)}
              />
            )}
          </Box>
        );
      }
      return (
        <Text size="1rem" color={page.tableText} style={{ wordBreak: 'break-all' }}>
          {row[uid] || '—'}
        </Text>
      );
    };

    return (
      <>
        {detailsModal && (
          <SimpleModal
            small={small}
            toggleModal={() => setDetailsModal(null)}
            modalTitle="Details"
            textColor={page.primaryText}
            highlightColor={page.highlight}
            modalBg={page.modalBg}
            closeLabel="Close"
            desktopMinWidth="32rem"
          >
            <Box pad={{ vertical: '1rem' }} gap="0.75rem" direction="column">
              {[
                { label: 'Match Type', value: detailsModal.match_type },
                { label: 'Match Timeframe', value: detailsModal.match_timeframe },
                { label: 'Channel Link', value: detailsModal.channel_link },
                { label: 'YouTube Claim ID', value: detailsModal.youtube_claim_id },
              ].map(({ label, value }) => (
                <Box key={label} direction="column" gap="0.25rem">
                  <Text size="0.85rem" color={page.secondaryText} weight={600}>{label}</Text>
                  <Box
                    background={badgeBg(isDarkMode, page)}
                    border={{ color: page.tableBorder, size: '1px' }}
                    round="0.5rem"
                    pad={{ vertical: '0.4rem', horizontal: '0.6rem' }}
                  >
                    <Text size="0.9rem" color={page.tableText} style={{ wordBreak: 'break-all' }}>
                      {value || '—'}
                    </Text>
                  </Box>
                </Box>
              ))}
            </Box>
          </SimpleModal>
        )}

        {notesModal && (
          <SimpleModal
            small={small}
            toggleModal={() => setNotesModal(null)}
            modalTitle="Notes"
            textColor={page.primaryText}
            highlightColor={page.highlight}
            modalBg={page.modalBg}
            closeLabel="Close"
            desktopMinWidth="32rem"
          >
            <Box pad={{ vertical: '1rem' }}>
              <Text
                size="0.95rem"
                color={page.primaryText}
                style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
              >
                {notesModal.rejection_note || '—'}
              </Text>
            </Box>
          </SimpleModal>
        )}

        <TableWrapper
          hidePagination
          small={small}
          header="Line items"
          boxPad={small ? '0.5rem' : '1rem'}
          isPaged={false}
          tableControls={null}
          width="100%"
        >
          <Box width="100%" direction="column">
            {!small && (
              <Box
                width="100%"
                wrap={false}
                direction="row"
                border={{
                  color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
                }}
              >
                {LINE_ITEMS_HEADER_MAP.map(({ uid, display, size }) => (
                  <Box
                    key={uid}
                    width={size}
                    pad={{
                      horizontal: '0.5rem',
                      top: '1rem',
                      bottom: '0.5rem',
                    }}
                  >
                    {renderTableHeader(display, small)}
                  </Box>
                ))}
              </Box>
            )}
            <Box
              width="100%"
              direction="column"
              style={{
                maxHeight: '50vh',
                overflowY: 'auto',
              }}
            >
              {items.map((row, i) => (
                <Box
                  key={[
                    row.asset_id,
                    row.infringing_url,
                    row.original_link,
                    row.claim_action_type ?? row.claim_type,
                    row.match_type,
                    row.match_timeframe,
                    row.channel_link,
                    row.youtube_claim_id,
                    row.rejection_note,
                  ].join('||')}
                  direction={small ? 'column' : 'row'}
                  align={small ? 'start' : 'center'}
                  border={i < items.length - 1 && {
                    color: page.tableBorder,
                    size: '1px',
                    style: 'solid',
                    side: 'bottom',
                  }}
                  height={{ min: 'fit-content' }}
                  pad={{ vertical: '1rem', right: '0.5rem' }}
                  background={page.tableBg}
                  width="100%"
                >
                  {LINE_ITEMS_HEADER_MAP.map(({ uid, display, size }) => (
                    <Box
                      key={uid}
                      width={small ? '100%' : size}
                      pad={{
                        horizontal: small ? '0' : '0.5rem',
                        vertical: small ? '0.25rem' : '0',
                      }}
                      direction="row"
                      gap="0.5rem"
                    >
                      {small && display && (
                        <Text size="0.875rem" color={page.tableHeader}>
                          {display}
                          :
                        </Text>
                      )}
                      {renderDataPoint(uid, row)}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </TableWrapper>
      </>
    );
  };

  const renderPageContent = () => {
    if (loading) {
      return (
        <Box flex align="center" justify="center" background={usePageBg}>
          <Spinning size="large" color={page.highlight} />
        </Box>
      );
    }

    if (paymentPending) {
      return (
        <Box flex background={usePageBg} pad={small ? '1.5rem' : '2rem'}>
          <Text color={page.primaryText}>
            Manual claim requests are unavailable while payment is pending.
          </Text>
        </Box>
      );
    }

    if (!detail) {
      return (
        <Box flex align="center" background={usePageBg} pad={small ? '1.5rem' : '2rem'}>
          <NoDataPlaceholder
            loading={false}
            width="100%"
            label="We could not load this request. It may have been removed or the link is invalid."
            textColor={page.primaryText}
            loaderColor={page.highlight}
          />
        </Box>
      );
    }

    const leftInfo = [
      { label: 'Request ID', value: detail.id },
      {
        label: 'Status',
        value: (
          <ManualClaimStatusPill status={detail.status} page={page} />
        ),
      },
      { label: 'Submitted', value: createdAt ? buildDateTimeLabel(createdAt) : '—' },
      // { label: 'Source', value: sourceLabel },
    ];

    return (
      <Box
        flex
        direction="column"
        width="100%"
        align="start"
        background={usePageBg}
        pad={{ bottom: small ? '1.5rem' : '2rem' }}
      >
        <Box
          direction={small ? 'column' : 'row'}
          pad="1.5rem"
          gap="1.5rem"
          width="100%"
          align={small ? 'start' : 'stretch'}
        >
          <Box
            flex="grow"
            direction="column"
            gap="0.75rem"
            pad="1rem"
            background={useSectionBg}
            round="var(--app-radius)"
            width={small ? '100%' : { max: '50%' }}
            style={{ minWidth: 0, boxShadow: 'var(--elevation)' }}
          >
            <Text size="1.25rem" weight={600} color={page.primaryText}>
              Request info
            </Text>
            <Box direction="column" gap="0.5rem">
              {leftInfo.map(({ label, value }) => (
                <Box key={label} direction="row" gap="0.5rem" align="center" wrap>
                  <Text weight={500} color={page.primaryText}>
                    {label}
                    :
                  </Text>
                  {typeof value === 'string' || typeof value === 'number' ? (
                    <Text
                      color={page.primaryText}
                      size="1rem"
                      style={
                        label === 'Request ID'
                          ? {
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                            wordBreak: 'break-all',
                          }
                          : undefined
                      }
                    >
                      {value || '—'}
                    </Text>
                  ) : (
                    value
                  )}
                </Box>
              ))}
            </Box>
          </Box>
          <Box
            flex="grow"
            direction="column"
            gap="0.75rem"
            pad="1rem"
            background={useSectionBg}
            round="var(--app-radius)"
            width={small ? '100%' : { max: '50%' }}
            style={{ minWidth: 0, boxShadow: 'var(--elevation)' }}
          >
            <Text size="1.25rem" weight={600} color={page.primaryText}>
              Summary
            </Text>
            <Box direction="column" gap="0.5rem">
              {Object.keys(itemSummary).length > 0 ? (
                Object.entries(itemSummary).map(([k, v]) => (
                  <Box key={k} direction="row" gap="0.5rem" align="center" wrap>
                    <StyledStatusBadge
                      background={STATUS_COLOR_MAP[k]?.background || useSectionBg}
                    >
                      <Text
                        size="1rem"
                        color={STATUS_COLOR_MAP[k]?.text || page.primaryText}
                        weight={600}
                      >
                        {Case.capital(k)}
                      </Text>
                    </StyledStatusBadge>
                    <Text color={page.primaryText}>{v}</Text>
                  </Box>
                ))
              ) : (
                <Text color={page.primaryText}>
                  {items.length}
                  {' '}
                  line item
                  {items.length === 1 ? '' : 's'}
                </Text>
              )}
              {rejectionNote && (
                <Box gap="0.35rem" margin={{ top: '0.25rem' }}>
                  <Text weight={500} color={page.primaryText} size="0.95rem">
                    Request note
                  </Text>
                  <Text color={page.primaryText} size="0.95rem" style={{ whiteSpace: 'pre-wrap' }}>
                    {rejectionNote}
                  </Text>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        {renderLineItemsTable()}
      </Box>
    );
  };

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Manual claim status & details',
        subTitle: 'Content ID',
        bannerChildrenPosition: 'bottom',
        bannerChildren: !loading && (
          <Box width="fit-content" direction="row" gap="1rem">
            <AppButton
              overrideHover
              onClick={() => navigate(paths.productAllClaimsList, { state: { view: 'manual' } })}
              label="Go back"
              level="tableControls"
              bgColor={isDarkMode ? button.primaryBg : page.altBg}
              color={isDarkMode ? button.primaryText : page.primaryText}
              icon={<Previous size="0.8rem" color={isDarkMode ? button.primaryText : page.primaryText} />}
            />
            <AppButton
              overrideHover
              onClick={() => id && fetchDetail(id)}
              label="Refresh"
              level="tableControls"
              bgColor={isDarkMode ? button.primaryBg : page.altBg}
              color={isDarkMode ? button.primaryText : page.primaryText}
              icon={<Refresh size="0.9rem" color={isDarkMode ? button.primaryText : page.primaryText} />}
              disabled={!id || paymentPending}
            />
          </Box>
        ),
      }}
    >
      <Seo />
      <Box className="tour-manual-claim-request-detail" fill width="100%">
        {renderPageContent()}
      </Box>
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    detail: state.contentIdClaims.manualClaimRequestDetail,
    loading: state.fetchLoader.dataLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      fetchDetail: initiateFetchManualClaimRequestDetail,
      clearDetail: clearManualClaimRequestDetail,
    },
    dispatch,
  );
}

ManualClaimRequestDetailPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  detail: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  fetchDetail: PropTypes.func.isRequired,
  clearDetail: PropTypes.func.isRequired,
  paymentPending: PropTypes.bool.isRequired,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withProductAuth(ManualClaimRequestDetailPage));
